import { BiSolidLockAlt } from "react-icons/bi";
import { HiOutlineDesktopComputer } from 'react-icons/hi';
import { HiMiniDevicePhoneMobile } from 'react-icons/hi2';
import { BsFiletypePdf } from 'react-icons/bs';
import { MdPrivacyTip } from 'react-icons/md';
import { AiFillDatabase } from 'react-icons/ai';
import { AiOutlineSafetyCertificate } from 'react-icons/ai';
import { LiaFileDownloadSolid } from 'react-icons/lia';
import StartYourFreeTrial from "../../components/StartYourFreeTrial";
import { Helmet } from "react-helmet-async";

const Security = () => {
  return (
    <div>
      {/* SEO */}
      <Helmet>
        <title>My Church Directory Security | Online Church Directory Software</title>

        <meta
          name="description"
          content="Explore powerful church directory features including member management, mobile apps, email and text messaging, printable directories, and secure online access. Manage your church directory with ease."
        />
      </Helmet>

      {/* Hero */}
      <section className="bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="inline-flex bg-sky-100 text-sky-700 rounded-full px-4 py-1 text-sm font-medium">
              Security & Privacy
            </span>

            <h1 className="different_heading text-4xl lg:text-5xl font-bold mt-5 text-slate-900">
              Enterprise-Level Security for Your Church
            </h1>

            <p className="mt-6 text-lg text-slate-600 leading-8">
              Protect your members with secure authentication, encrypted cloud
              storage, role-based access, and optional two-factor authentication.
            </p>
          </div>

          <div>
            <img
              src="../images/data-security.png"
              alt="Church Security"
              className="rounded-3xl shadow-xl w-full"
            />
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="different_heading text-3xl font-bold">
              Built to Keep Your Church Data Safe
            </h2>

            <p className="text-slate-600 mt-3">
              Every feature is designed to protect your church information while
              keeping access simple for administrators and members.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="border rounded-2xl p-6">
              <BiSolidLockAlt className="text-4xl text-secondary mb-4" />
              <h3 className="font-semibold mb-2">Secure Authentication</h3>
              <p className="text-slate-600 text-sm">
                Verified member access with secure administrator controls.
              </p>
            </div>

            <div className="border rounded-2xl p-6">
              <HiOutlineDesktopComputer className="text-4xl text-secondary mb-4" />
              <h3 className="font-semibold mb-2">Online Directory</h3>
              <p className="text-slate-600 text-sm">
                Only approved members can access your private directory.
              </p>
            </div>

            <div className="border rounded-2xl p-6">
              <HiMiniDevicePhoneMobile className="text-4xl text-secondary mb-4" />
              <h3 className="font-semibold mb-2">Mobile Security</h3>
              <p className="text-slate-600 text-sm">
                Secure mobile access with optional device verification.
              </p>
            </div>

            <div className="border rounded-2xl p-6">
              <BsFiletypePdf className="text-4xl text-secondary mb-4" />
              <h3 className="font-semibold mb-2">Protected PDFs</h3>
              <p className="text-slate-600 text-sm">
                Share printable directories with password protection.
              </p>
            </div>

            <div className="border rounded-2xl p-6">
              <MdPrivacyTip className="text-4xl text-secondary mb-4" />
              <h3 className="font-semibold mb-2">Privacy First</h3>
              <p className="text-slate-600 text-sm">
                Your member information is never sold or used for marketing.
              </p>
            </div>

            <div className="border rounded-2xl p-6">
              <AiFillDatabase className="text-4xl text-secondary mb-4" />
              <h3 className="font-semibold mb-2">Cloud Storage</h3>
              <p className="text-slate-600 text-sm">
                Private cloud infrastructure with automatic backups.
              </p>
            </div>

            <div className="border rounded-2xl p-6">
              <AiOutlineSafetyCertificate className="text-4xl text-secondary mb-4" />
              <h3 className="font-semibold mb-2">Two-Factor Authentication</h3>
              <p className="text-slate-600 text-sm">
                Add an extra layer of protection for every login.
              </p>
            </div>

            <div className="border rounded-2xl p-6">
              <BiSolidLockAlt className="text-4xl text-secondary mb-4" />
              <h3 className="font-semibold mb-2">Administrator Control</h3>
              <p className="text-slate-600 text-sm">
                Instantly enable, disable, or revoke member access.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Data Protection */}
      <section className="bg-slate-50 py-20">
        <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
          <img
            src="../images/security-page-bg.png"
            alt="Data Protection"
            className="rounded-3xl shadow-lg"
          />

          <div>
            <h2 className="different_heading text-3xl font-bold">
              Your Data Stays Safe and Private
            </h2>

            <p className="text-slate-600 mt-5 leading-8">
              Our platform follows modern security practices to protect your church
              directory, member records, and communication while giving
              administrators complete control over access.
            </p>

            <ul className="mt-8 space-y-3 text-slate-700">
              <li>✓ Secure administrator login</li>
              <li>✓ Member verification</li>
              <li>✓ Password-protected directories</li>
              <li>✓ Automatic cloud backups</li>
              <li>✓ Private infrastructure</li>
              <li>✓ Optional two-factor authentication</li>
            </ul>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6">
          <div className="rounded-3xl bg-secondary text-white p-12 text-center">
            <LiaFileDownloadSolid className="text-6xl mx-auto mb-6" />

            <h2 className="different_heading text-3xl font-bold">
              Security You Can Trust
            </h2>

            <p className="mt-4 opacity-90 max-w-2xl mx-auto">
              We continuously improve our security practices so your church can
              confidently manage member information and communication.
            </p>
          </div>
        </div>
      </section>

      <StartYourFreeTrial />
    </div>
  )
}

export default Security